import { useQueries } from '@tanstack/react-query'
import Image from 'next/image'
import { useMemo } from 'react'
import PortalContainer from '../../../../components/container/PortalContainer'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useTranslation from '../../../../modules/i18n/useTranslation'
import client from '../../../../modules/sanityClient'
import { buildSanityImage } from '../../../../modules/utils'
import {
  MKT_EVENT_MAIN_QUERY,
  MKT_HOME_CHALLENGE_QUERY,
  MKT_HOME_PRICING_SUCCESS_QUERY,
  MKT_HOME_PROFILE_QUERY,
  MKT_HOME_TUTOR_QUERY,
  MKT_MAIN_CHALLENGE_QUERY,
} from '../../../../sanity/groqQueries'
import {
  MKT_EVENT_MAIN_QUERYResult,
  MKT_HOME_CHALLENGE_QUERYResult,
  MKT_HOME_PRICING_SUCCESS_QUERYResult,
  MKT_HOME_PROFILE_QUERYResult,
  MKT_HOME_TUTOR_QUERYResult,
  MKT_MAIN_CHALLENGE_QUERYResult,
} from '../../../../sanity/types'

export const fetchHomeChallenge = async (): Promise<MKT_HOME_CHALLENGE_QUERYResult> => {
  const data: MKT_HOME_CHALLENGE_QUERYResult = await client.fetch(MKT_HOME_CHALLENGE_QUERY)
  return data
}

export const fetchHomeContents = async (): Promise<MKT_HOME_TUTOR_QUERYResult> => {
  const data: MKT_HOME_TUTOR_QUERYResult = await client.fetch(MKT_HOME_TUTOR_QUERY)
  return data
}

export const fetchPricingSuccess = async (): Promise<MKT_HOME_PRICING_SUCCESS_QUERYResult> => {
  const data: MKT_HOME_PRICING_SUCCESS_QUERYResult = await client.fetch(MKT_HOME_PRICING_SUCCESS_QUERY)
  return data
}

export const fetchProfile = async (): Promise<MKT_HOME_PROFILE_QUERYResult> => {
  const data: MKT_HOME_PROFILE_QUERYResult = await client.fetch(MKT_HOME_PROFILE_QUERY)
  return data
}

export const fetchMainChallenge = async (): Promise<MKT_MAIN_CHALLENGE_QUERYResult> => {
  const data: MKT_MAIN_CHALLENGE_QUERYResult = await client.fetch(MKT_MAIN_CHALLENGE_QUERY)
  return data
}

export const fetchMainEvent = async (): Promise<MKT_EVENT_MAIN_QUERYResult> => {
  const data: MKT_EVENT_MAIN_QUERYResult = await client.fetch(MKT_EVENT_MAIN_QUERY)
  return data
}

const MarketingBanners = () => {
  const { t } = useTranslation()

  // 병렬로 쿼리를 실행합니다.
  const queryResults = useQueries({
    queries: [
      {
        queryKey: ['mktHomeChallengeBanner'],
        queryFn: () => fetchHomeChallenge(),
      },
      {
        queryKey: ['mktHomeTutorBanner'],
        queryFn: () => fetchHomeContents(),
      },
      {
        queryKey: ['mktPricingSuccessBanner'],
        queryFn: () => fetchPricingSuccess(),
      },
      {
        queryKey: ['mktProfileBanner'],
        queryFn: () => fetchProfile(),
      },
      {
        queryKey: ['mktMainChallengeBanner'],
        queryFn: () => fetchMainChallenge(),
      },
    ],
  })

  const isFetching = queryResults.some((query) => query.isFetching)

  const [
    mktHomeChallengeBanner,
    mktHomeTutorBanner,
    mktPricingSuccessBanner,
    mktProfileBanner,
    mktMainChallengeBanner,
  ] = queryResults

  const { data: mktHomeChallengeBannerData } = mktHomeChallengeBanner
  const { data: mktHomeTutorBannerData } = mktHomeTutorBanner
  const { data: mktPricingSuccessBannerData } = mktPricingSuccessBanner
  const { data: mktProfileBannerData } = mktProfileBanner
  const { data: mktMainChallengeBannerData } = mktMainChallengeBanner

  return (
    <PortalContainer>
      <div className="Hero4-32Bd mt-[24px]">[Marketing] 배너 관리</div>
      <a
        href={'https://ringle-studio.vercel.app/studio/desk/marketing'}
        target="_blank"
        rel="noreferrer"
        className="Body1-16Md text-blue-400 underline"
      >
        Studio 바로가기 {'>>'}
      </a>
      <div className="mt-[24px] flex flex-col gap-[16px]">
        <div className="Headline3-22Bd">홈 화면 {'>'} 챌린지 배너</div>
        <BannerComponent data={mktHomeChallengeBannerData} isFetching={isFetching} isGlobal={false} />
        <BannerComponent
          data={mktHomeChallengeBannerData}
          isFetching={isFetching}
          isGlobal={false}
          isInitialMobile={true}
        />
      </div>
      <div className="mt-[24px] flex flex-col gap-[16px]">
        <div className="Headline3-22Bd">홈 화면 {'>'} 튜터 배너</div>
        <BannerComponent data={mktHomeTutorBannerData} isFetching={isFetching} isGlobal={false} />
        <BannerComponent
          data={mktHomeTutorBannerData}
          isFetching={isFetching}
          isGlobal={false}
          isInitialMobile={true}
        />
      </div>
      <div className="mt-[24px] flex flex-col gap-[16px]">
        <div className="Headline3-22Bd">챌린지 메인 {'>'} 히어로 배너</div>
        <BannerComponent
          data={mktMainChallengeBannerData}
          isFetching={isFetching}
          isGlobal={false}
          desktopMaxWidth={1040}
        />
        <BannerComponent
          data={mktMainChallengeBannerData}
          isFetching={isFetching}
          isGlobal={false}
          isInitialMobile={true}
          mobileMaxWidth={360}
        />
      </div>
      <div className="mt-[24px] flex flex-col gap-[16px]">
        <div className="Headline3-22Bd">결제 성공 페이지 {'>'} 배너</div>
        <BannerComponent
          data={mktPricingSuccessBannerData}
          isFetching={isFetching}
          isGlobal={false}
          desktopMaxWidth={405}
        />
        <BannerComponent
          data={mktPricingSuccessBannerData}
          isFetching={isFetching}
          isGlobal={false}
          isInitialMobile={true}
          mobileMaxWidth={335}
        />
      </div>
    </PortalContainer>
  )
}

export default MarketingBanners

const BannerComponent = ({
  data,
  isFetching,
  isGlobal,
  isInitialMobile = false,
  desktopMaxWidth = 688,
  mobileMaxWidth = 328,
}) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const isMobile = useIsMobile(isInitialMobile, 640)

  const globalArray = useMemo(
    () =>
      data && !isFetching && isMobile
        ? isGlobal
          ? t([data.ko_mobile, data.en_mobile, '', data.chs_mobile, data.cht_mobile, data.ja_mobile, data.vi_mobile])
          : t([data.ko_mobile, data.en_mobile, '', data.en_mobile, data.en_mobile, data.en_mobile, data.en_mobile])
        : isGlobal
        ? t([
            data.ko_desktop,
            data.en_desktop,
            '',
            data.chs_desktop,
            data.cht_desktop,
            data.ja_desktop,
            data.vi_desktop,
          ])
        : t([data.ko_desktop, data.en_desktop, '', data.en_desktop, data.en_desktop, data.en_desktop, data.en_desktop]),
    [isMobile, isGlobal, data]
  )

  if (isFetching || !data) return
  return (
    data &&
    !isFetching && (
      <div
        style={{ maxWidth: isMobile ? `${mobileMaxWidth}px` : `${desktopMaxWidth}px` }}
        className="cursor-pointer"
        onClick={() => router.push(globalArray.link as any)}
      >
        <Image
          alt={data?.ko_desktop.alt}
          loading="lazy"
          className={'mx-auto rounded-[8px] object-cover'}
          src={buildSanityImage(globalArray).url()}
          style={{
            width: '100%',
            height: '100%',
          }}
          width={688}
          height={688}
          quality={100}
        />
      </div>
    )
  )
}
